import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Calculator, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useCountry } from "@/lib/country";

export default function Costs() {
  const [treatments, setTreatments] = useState([]);
  const [treatment, setTreatment] = useState("");
  const { country, currency, countries } = useCountry();
  const [countrySlug, setCountrySlug] = useState(country?.slug || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    http.get("/treatments").then(r => setTreatments(r.data));
  }, []);
  useEffect(() => { setCountrySlug(country?.slug || ""); }, [country]);

  const estimate = async () => {
    if (!treatment) return;
    setLoading(true);
    try {
      const { data } = await http.get("/costs/estimate", { params: { treatment, country: countrySlug, currency: currency.code } });
      setResult(data);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-20">
      <div className="overline mb-3">Estimated Treatment Costs</div>
      <h1 className="font-serif text-4xl md:text-6xl text-[#F3E9EC]">Get an indicative cost estimate for treatment in India</h1>
      <p className="mt-5 text-[#F3E9EC]/70 max-w-2xl">All estimates are indicative. Submit an enquiry for a personalised quotation from our care coordinators.</p>

      <div className="mt-12 mp-card p-8">
        <div className="flex items-center gap-3 mb-6">
          <Calculator className="w-5 h-5 text-[#B47A9A]"/>
          <span className="overline">Cost Calculator</span>
          <span className="ml-auto text-xs text-[#F3E9EC]/50 font-mono">Displaying in <span className="text-[#B47A9A]">{currency.code}</span></span>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#F3E9EC]/60 mb-2 block">Treatment</label>
            <select value={treatment} onChange={e=>setTreatment(e.target.value)} className="mp-select" data-testid="cost-calculator-select-treatment">
              <option value="">Select a treatment</option>
              {treatments.map(t => <option key={t.id} value={t.slug}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#F3E9EC]/60 mb-2 block">Patient Country</label>
            <select value={countrySlug} onChange={e=>setCountrySlug(e.target.value)} className="mp-select" data-testid="cost-calculator-select-country">
              <option value="">Any</option>
              {countries.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <button onClick={estimate} disabled={!treatment || loading} className="btn-primary mt-6 px-7 py-3 disabled:opacity-50" data-testid="cost-calculator-estimate-btn">
          {loading ? "Calculating…" : "Get Estimate"}
        </button>
      </div>

      {result && (
        <div className="mt-8 mp-card p-8" data-testid="cost-calculator-result">
          {result.found ? (
            <>
              <div className="overline mb-2">Indicative Cost in India</div>
              <div className="font-serif text-4xl md:text-5xl text-[#F3E9EC]">₹ {result.min.toLocaleString()} – {result.max.toLocaleString()}</div>
              {result.converted && result.converted.code !== "INR" && (
                <div className="mt-3 text-xl text-[#B47A9A] font-mono" data-testid="cost-converted">
                  ≈ {result.converted.symbol} {result.converted.min.toLocaleString()} – {result.converted.max.toLocaleString()} {result.converted.code}
                  <span className="ml-3 text-xs text-[#F3E9EC]/45">{result.converted.rate_note}</span>
                </div>
              )}
              <p className="mt-5 text-xs text-[#F3E9EC]/50">{result.disclaimer}</p>
              <div className="mt-6 flex gap-4">
                <Link to="/enquiry" className="btn-primary px-6 py-3 font-medium flex items-center gap-2">Get Personalised Quotation <ArrowRight className="w-4 h-4"/></Link>
              </div>
            </>
          ) : (
            <>
              <p className="text-[#F3E9EC]/80">{result.message}</p>
              <Link to="/enquiry" className="btn-primary px-6 py-3 mt-4 inline-block">Request a personalised quote</Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}
